import { useState } from 'react';
import type { ClientFlag, ResolvedFlag, Severity } from '../api/types.ts';
import { formatDate } from '../dates.ts';

/**
 * Pinned client flags.
 *
 * These exist because staff have been writing warnings into client name
 * fields for want of anywhere else to put them. The requirement is not that
 * the flags are displayed — it is that a case manager cannot miss a Critical
 * one while glancing at a tablet, standing up, one-handed, in a lobby.
 *
 * So:
 *   - Critical renders above the client's name, full width, headline *and*
 *     detail. Never a count, never a chip, never behind a tap. A count is
 *     something you can read without absorbing.
 *   - Critical is never collapsed and never dismissible. Warnings and Notes
 *     collapse, because they are the ones that would otherwise push the name
 *     off the screen.
 *   - Never colour alone. Icon, the severity word in full, and a heavy edge —
 *     a red tint on an angled screen in a sunlit dayroom is not a signal.
 *   - Stacking is normal. Two Criticals is a real case in the data and gets
 *     two full bands, not a summary.
 *
 * **Which band a flag lands in is `flag.band`, not `flag.severity`.** The
 * database derives it: Health and Safety are Critical whatever severity was
 * typed. The seed carries the case this is for — "Two-person visit required —
 * prior incident on a home visit, do not attend alone", entered as a Warning
 * by someone who did not reach for Critical, and therefore hidden behind a tap
 * until this rule existed. Severity still orders items within a band, and the
 * stored value is never rewritten.
 */

/** How far ahead a dated flag starts showing expanded, when it says nothing. */
const DEFAULT_LEAD_DAYS: Record<Severity, number> = {
  Critical: 30,
  Warning: 14,
  Note: 7,
};

/**
 * Prominence follows proximity. A hearing three months out can sit behind a
 * disclosure control; the same hearing in three days cannot.
 *
 * Past dates stay surfaced rather than dropping away: a flag whose date has
 * just passed is usually the one someone still needs to act on.
 */
export function isImminent(flag: ClientFlag): boolean {
  if (flag.days_until === null) return false;
  const lead = flag.surface_days_before ?? DEFAULT_LEAD_DAYS[flag.severity] ?? 14;
  return flag.days_until <= lead;
}

function CriticalIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-6 shrink-0 fill-current">
      <path d="M12 2 1 21h22L12 2Zm0 6 1 7h-2l1-7Zm0 9.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5Z" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 shrink-0 fill-current">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1 5h2v7h-2V7Zm0 9h2v2h-2v-2Z" />
    </svg>
  );
}

function ResolvedIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 shrink-0 fill-current">
      <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.2 14.4-4.2-4.2 1.4-1.4 2.8 2.8 6-6 1.4 1.4-7.4 7.4Z" />
    </svg>
  );
}

/** A `date` column, not an instant — see dates.ts for why that matters. */
const since = formatDate;

/**
 * "in 3 days", "today", "3 days ago" — the number of days is the part that
 * decides what someone does, so it leads and the date follows it.
 */
function when(flag: ClientFlag): string | null {
  if (flag.event_date === null || flag.days_until === null) return null;
  const d = flag.days_until;
  const rel =
    d === 0 ? 'today'
    : d === 1 ? 'tomorrow'
    : d === -1 ? 'yesterday'
    : d > 0 ? `in ${d} days`
    : `${Math.abs(d)} days ago`;
  return `${rel} · ${since(flag.event_date)}`;
}

function EventDate({ flag, urgent }: { flag: ClientFlag; urgent: boolean }) {
  const text = when(flag);
  if (!text) return null;
  return (
    <p className={urgent ? 'mt-1.5 text-[0.9375rem] font-bold' : 'mt-1 text-sm font-semibold'}>
      {text}
    </p>
  );
}

export function CriticalFlags({ flags }: { flags: ClientFlag[] }) {
  if (flags.length === 0) return null;

  return (
    <div role="alert" className="space-y-px">
      {flags.map((flag, i) => (
        <div
          key={`${flag.headline}-${i}`}
          className="bg-critical-bg text-critical-fg border-l-8 border-critical-edge px-4 py-3.5"
        >
          <div className="flex gap-3">
            <CriticalIcon />
            <div className="min-w-0">
              <p className="text-xs font-bold tracking-[0.14em] uppercase opacity-90">
                Critical · {flag.type}
              </p>
              <p className="mt-0.5 text-lg leading-tight font-bold">{flag.headline}</p>
              {flag.detail && (
                <p className="mt-1.5 text-[0.9375rem] leading-snug opacity-95">
                  {flag.detail}
                </p>
              )}
              <EventDate flag={flag} urgent />
              <p className="mt-1.5 text-xs opacity-75">Flagged {since(flag.since)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function LesserFlags({ flags }: { flags: ClientFlag[] }) {
  // A dated flag inside its lead time opens the disclosure by itself. Someone
  // who then collapses it has decided; that decision holds for the visit.
  const imminent = flags.filter(isImminent);
  const [open, setOpen] = useState(imminent.length > 0);
  if (flags.length === 0) return null;

  const warnings = flags.filter((f) => f.band === 'Warning').length;
  const notes = flags.length - warnings;
  const summary = [
    warnings > 0 ? `${warnings} warning${warnings > 1 ? 's' : ''}` : null,
    notes > 0 ? `${notes} note${notes > 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="border-t border-neutral-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[52px] w-full items-center gap-2.5 px-4 py-3 text-left
                   text-warning-fg active:bg-neutral-100"
      >
        <WarningIcon />
        <span className="font-semibold">{summary}</span>
        {imminent.length > 0 && (
          <span className="rounded-full bg-warning-edge px-2 py-0.5 text-xs font-bold
                           tracking-wide text-white uppercase">
            {imminent.length} dated
          </span>
        )}
        <span className="ml-auto text-sm text-neutral-500">
          {open ? 'Hide' : 'Show'}
        </span>
      </button>

      {open && (
        <ul className="space-y-px pb-1">
          {flags.map((flag, i) => {
            const isWarning = flag.band === 'Warning';
            return (
              <li
                key={`${flag.headline}-${i}`}
                className={
                  isWarning
                    ? 'bg-warning-bg text-warning-fg border-l-8 border-warning-edge px-4 py-3'
                    : 'bg-note-bg text-note-fg border-l-8 border-note-edge px-4 py-3'
                }
              >
                <p className="text-xs font-bold tracking-[0.12em] uppercase opacity-80">
                  {flag.band} · {flag.type}
                </p>
                <p className="mt-0.5 font-semibold">{flag.headline}</p>
                {flag.detail && (
                  <p className="mt-1 text-[0.9375rem] leading-snug opacity-90">
                    {flag.detail}
                  </p>
                )}
                <EventDate flag={flag} urgent={isImminent(flag)} />
                <p className="mt-1 text-xs opacity-70">Flagged {since(flag.since)}</p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Health and Safety flags that someone has lifted.
 *
 * Out of the Critical band — they no longer apply — but still on the record
 * and still findable. A case manager should be able to see that a two-person
 * visit requirement existed, was lifted in March, and by whom. Collapsed by
 * default, because this is history rather than instruction; never removed,
 * because "there was never a reason to worry" and "the reason was resolved"
 * are different answers and only one of them is true.
 */
export function ResolvedSafetyFlags({ flags }: { flags: ResolvedFlag[] }) {
  const [open, setOpen] = useState(false);
  if (flags.length === 0) return null;

  return (
    <section className="border-t border-neutral-200">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[52px] w-full items-center gap-2.5 px-4 py-3 text-left
                   text-neutral-600 active:bg-neutral-100"
      >
        <ResolvedIcon />
        <span className="font-semibold">
          {flags.length} resolved health or safety {flags.length > 1 ? 'flags' : 'flag'}
        </span>
        <span className="ml-auto text-sm text-neutral-500">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <ul className="divide-y divide-neutral-200 border-t border-neutral-200">
          {flags.map((flag, i) => (
            <li key={`${flag.headline}-${i}`} className="px-4 py-3">
              <p className="text-xs font-bold tracking-[0.12em] text-neutral-500 uppercase">
                Resolved · {flag.type}
              </p>
              <p className="mt-0.5 font-semibold text-neutral-700 line-through
                            decoration-neutral-400">
                {flag.headline}
              </p>
              {flag.detail && (
                <p className="mt-1 text-[0.9375rem] leading-snug text-neutral-600">
                  {flag.detail}
                </p>
              )}
              <p className="mt-1.5 text-sm text-neutral-700">
                Lifted {since(flag.resolved_on)}
                {flag.resolved_by && ` by ${flag.resolved_by}`}
              </p>
              {flag.resolution_note && (
                <p className="mt-0.5 text-sm text-neutral-600">{flag.resolution_note}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
